"use client";

import React, { useEffect, useRef } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function BeginYourTransformation() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const {
    ref: contentRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let animationFrameId: number;
    let particles: {
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
    }[] = [];
    const resize = () => {
      canvas.width = canvas.parentElement?.clientWidth || window.innerWidth;
      canvas.height = canvas.parentElement?.clientHeight || 400;
      initParticles();
    };
    const initParticles = () => {
      particles = [];
      const density = window.innerWidth < 768 ? 20000 : 12000;
      const numParticles = Math.floor(canvas.width * canvas.height / density);
      for (let i = 0; i < numParticles; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.4,
          vy: (Math.random() - 0.5) * 0.4 - 0.2,
          size: Math.random() * 1.5 + 0.5
        });
      }
    };
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "rgba(23, 85, 253, 0.15)";
      ctx.strokeStyle = "rgba(23, 85, 253, 0.08)";
      particles.forEach((p, i) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }
      });
      animationFrameId = requestAnimationFrame(draw);
    };
    window.addEventListener("resize", resize);
    resize();
    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  return <section data-section-id="176503" id="global-presence-scale" className="bg-background text-foreground relative flex min-h-[60vh] items-center justify-center overflow-hidden py-24 lg:py-32">
      {}
      <canvas ref={canvasRef} className="absolute inset-0 z-0 h-full w-full opacity-30 pointer-events-none" aria-hidden="true" />

      {}
      <div ref={contentRef} className={`container relative z-10 mx-auto px-4 text-center transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
        <h2 className="mb-10 text-5xl font-default font-bold tracking-tighter text-foreground md:text-6xl lg:text-7xl">
          100+ Cities, 9 Countries
        </h2>
        
        <Button asChild size="lg" className="group h-16 rounded-[10px] px-10 text-lg font-default bg-primary text-primary-foreground shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl hover:bg-primary/90">
          <Link to="#" className="flex items-center gap-3">
            Experience Global Scale
            <ArrowRight className="h-6 w-6 transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
        </Button>
      </div>
    </section>;
}